import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetCoverRedoQuery } from '../../../../../../redux/api/fymAPI';
import { useIsDemoAccount } from '../../../../../accountSelector/useIsDemoAccount';
import { useRealtimePollInterval } from '../../../../../realtime/useRealtimePollInterval';
import { coverRedoBannerState, isCoverRedoState, type CoverRedoBannerState } from './coverRedoBannerState';
import { readDismissedCover, readSeenRun, writeDismissedCover, writeSeenRun } from './coverRedoMemory';

/** The poll while a redo runs and the socket is down (design kit-redo-cover c1). */
const WORKING_POLL_MS = 15_000;

/**
 * The cover redo state of one song for the Kit and the Posts tab (design kit-redo-cover a, c):
 * the state answer, the banner state, "Not now" for this cover, the close of an end card, and the
 * quiet line after "Not now". The demo artist reads nothing. The `ReleaseKit` tag of the song
 * follows `kit.asset.updated`, `reel.updated` and `catalog.song.updated`, so the answer stays live;
 * a 15 s poll runs only while a redo works and the socket is down.
 */
export function useCoverRedo(input: { accountId: number; catalogItemUuid: string }) {
  const { accountId, catalogItemUuid } = input;
  const isDemo = useIsDemoAccount();
  const [isWorking, setIsWorking] = useState(false);
  const pollingInterval = useRealtimePollInterval(isWorking ? WORKING_POLL_MS : 0);
  const { data: answer } = useGetCoverRedoQuery({ accountId, catalogItemUuid }, { skip: isDemo, pollingInterval });
  // An older API (or a test server) answers another shape: the banner then shows nothing.
  const data = isCoverRedoState(answer) ? answer : undefined;
  const running = data?.run?.state === 'working';
  useEffect(() => {
    setIsWorking(running);
  }, [running]);
  const [memory, setMemory] = useState(() => ({
    dismissedCover: readDismissedCover(catalogItemUuid),
    seenRun: readSeenRun(catalogItemUuid),
  }));

  const banner: CoverRedoBannerState = useMemo(() => coverRedoBannerState(data, memory), [data, memory]);
  // After "Not now" one quiet line stays while the files are still out of date (a4).
  const showsQuietLine =
    banner === 'hidden' &&
    data !== undefined &&
    data.outOfDate &&
    memory.dismissedCover !== null &&
    memory.dismissedCover === (data.cover.currentUrl ?? '');
  const oldCoverUuids = useMemo(() => new Set((data?.items ?? []).map((item) => item.uuid)), [data]);

  const notNow = useCallback(() => {
    const cover = data?.cover.currentUrl ?? '';
    writeDismissedCover(catalogItemUuid, cover);
    setMemory((current) => ({ ...current, dismissedCover: cover }));
  }, [catalogItemUuid, data]);

  const closeEnd = useCallback(() => {
    const runId = data?.run?.runId;
    if (runId === undefined) {
      return;
    }
    writeSeenRun(catalogItemUuid, runId);
    setMemory((current) => ({ ...current, seenRun: runId }));
  }, [catalogItemUuid, data]);

  return { state: data, banner, showsQuietLine, oldCoverUuids, notNow, closeEnd };
}

export type CoverRedo = ReturnType<typeof useCoverRedo>;
